import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { AisReportAgeResult, useAisReportAge } from './useAISReportAge';
import { useSelectionState } from '../../components/SelectionStateProvider';
import { getTargetInfoName } from '@ais-ui/websockets';
import { useViewMode, ViewMode } from '../../components/ViewModeProvider';

interface TargetContextProps {
  children: ReactNode;
  isExpanded?: boolean;
  setIsExpanded?: Dispatch<SetStateAction<boolean>>;
}

interface TargetContextValue {
  isExpanded: boolean;
  setIsExpanded?: Dispatch<SetStateAction<boolean>>;
  canShowDetailedInformation: boolean;
  tab: 'base' | 'integrity' | 'register' | 'advancedVessel' | 'nextPort';
  setTab: Dispatch<
    SetStateAction<
      'base' | 'integrity' | 'register' | 'advancedVessel' | 'nextPort'
    >
  >;
  nextTargetName: string;
  name: string;
  aisReportAgeResult: AisReportAgeResult | null;
  onExit: () => void;
  multipleTargetsSelected: boolean;
}

const TargetContext = createContext<TargetContextValue | undefined>(undefined);

export const TargetProvider = ({
  children,
  isExpanded = false,
  setIsExpanded,
}: TargetContextProps) => {
  const { viewMode, setViewMode } = useViewMode();
  const { getAisReportAge } = useAisReportAge();
  const {
    currentTargetInfo,
    currentTargetType,
    targetInfoDetailsList,
    currentIndex,
    setTargetInfoDetailsList,
  } = useSelectionState();

  const canShowDetailedInformation = useMemo(() => {
    if (!currentTargetInfo) {
      return false;
    }

    if ('imo' in currentTargetInfo && currentTargetInfo.imo) {
      return true;
    }

    if (
      'basic' in currentTargetInfo &&
      currentTargetInfo.basic &&
      'vesselImoNumber' in currentTargetInfo.basic &&
      currentTargetInfo.basic.vesselImoNumber
    ) {
      return true;
    }

    return false;
  }, [currentTargetInfo]);

  const multipleTargetsSelected = useMemo(() => {
    return targetInfoDetailsList.length > 1;
  }, [targetInfoDetailsList]);

  const [tab, setTab] = useState<
    'base' | 'integrity' | 'register' | 'advancedVessel' | 'nextPort'
  >('base');

  // Get IMO from current target info
  const currentImo = useMemo(() => {
    if (!currentTargetInfo) {
      return undefined;
    }
    if ('imo' in currentTargetInfo) {
      return currentTargetInfo.imo;
    }
    return undefined;
  }, [currentTargetInfo]);

  // Automatically switch to 'base' tab if 'nextPort' tab becomes invalid
  useEffect(() => {
    if (tab === 'nextPort') {
      const isDetailView = viewMode === ViewMode.Detail;
      const hasNoImo = !currentImo;

      // If nextPort tab is selected but conditions are no longer valid, switch to base tab
      if (isDetailView || hasNoImo) {
        setTab('base');
      }
    }
  }, [tab, viewMode, currentImo]);

  const nextTargetName = useMemo(() => {
    if (!multipleTargetsSelected) {
      return '';
    }
    if (currentIndex < targetInfoDetailsList.length - 1) {
      const nextTargetInfo = targetInfoDetailsList[currentIndex + 1];
      // Check if extractedTargetInfo array is not empty before accessing [0]
      if (!nextTargetInfo?.extractedTargetInfo?.[0]) {
        return 'Unknown';
      }
      return getTargetInfoName(
        nextTargetInfo.extractedTargetInfo[0],
        nextTargetInfo.extractedTargetInfoType,
      );
    } else {
      const nextTargetInfo = targetInfoDetailsList[0];
      // Check if extractedTargetInfo array is not empty before accessing [0]
      if (!nextTargetInfo?.extractedTargetInfo?.[0]) {
        return 'Unknown';
      }
      return getTargetInfoName(
        nextTargetInfo.extractedTargetInfo[0],
        nextTargetInfo.extractedTargetInfoType,
      );
    }
  }, [currentIndex, multipleTargetsSelected, targetInfoDetailsList]);

  const name = useMemo(() => {
    if (!currentTargetInfo || !currentTargetType) {
      return '';
    }

    return getTargetInfoName(currentTargetInfo, currentTargetType);
  }, [currentTargetInfo, currentTargetType]);

  const aisReportAgeResult = useMemo(() => {
    if (!currentTargetInfo || !currentTargetType) {
      return null;
    }

    return getAisReportAge(currentTargetInfo, currentTargetType);
  }, [getAisReportAge, currentTargetInfo, currentTargetType]);

  if (!currentTargetInfo || !currentTargetType) {
    return null;
  }

  const onExit = () => {
    if (viewMode === ViewMode.Detail) {
      setViewMode(ViewMode.Normal);
    }

    setTargetInfoDetailsList([]);
  };

  return (
    <TargetContext.Provider
      value={{
        isExpanded,
        setIsExpanded,
        canShowDetailedInformation,
        tab,
        setTab,
        nextTargetName,
        name,
        aisReportAgeResult,
        onExit,
        multipleTargetsSelected,
      }}
    >
      {children}
    </TargetContext.Provider>
  );
};

export const useTargetContext = () => {
  const context = useContext(TargetContext);
  if (!context) {
    throw new Error('useTargetContext must be used within a TargetProvider');
  }
  return context;
};
